import { Box, Button, Chip, CircularProgress, Collapse, Portal } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import { FormattedMessage } from 'react-intl';
import globalMessages from '../../../../../../config/globalMessages';
import modelFieldMessages from '../../../../../../config/utils/modelFieldMessages';
import useTableFilters from '../../../../../components/Table/useTableFilters';
import { compileFilters } from '../../../../../components/Table/filterUtils';
import listFilters from '../../../../../components/Table/listFilters';
import '../../../../../components/Table/filterStyle.css';

const compileEntryFilters = (filters, configuration) => {
  const results = filters.map((filter) => compileFilters([filter], configuration));
  const appliedFilters = results.flatMap((result) => result.appliedFilters).map((filter) => ({ ...filter, color: `hsl(${(filter.id * 137.508) % 360}, 65%, 45%)` }));
  return { params: appliedFilters, appliedFilters, errors: results.flatMap((result) => result.errors) };
};

export default function EntryDataFilters({ configuration, onChange, loading, error, hasMatches, container }) {
  const filters = useTableFilters(configuration, onChange, compileEntryFilters);
  return (
    <Portal container={container} disablePortal={!container}>
      <Box
        className="tableFilterShell"
        sx={{
          mb: 1,
          '& .tableFilterPanel': { gridTemplateColumns: 'minmax(0, 1fr)' },
          '& .filterRow': { gridTemplateColumns: 'minmax(100px, 1fr) minmax(100px, 1fr) minmax(140px, 1.5fr) 32px' },
          '& .filterControl .MuiFormControl-root': { minWidth: 0 }
        }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Button className="tableFilterToggle" onClick={filters.toggleFilters} aria-expanded={filters.filtersOpen}>
            <FilterListIcon fontSize="small" />
            <FormattedMessage {...modelFieldMessages.filters} />
            {filters.validFilters > 0 && <span className="tableFilterCount">{filters.validFilters}</span>}
          </Button>
          {filters.filtersOpen && (
            <Button className="tableFilterAdd" variant="outlined" onClick={filters.addNewFilter}>
              <FormattedMessage {...modelFieldMessages.addFilter} />
            </Button>
          )}
          {filters.appliedFilters.map((filter) => (
            <Chip
              className="tableFilterChip"
              size="small"
              key={filter.id}
              style={{ borderLeft: `5px solid ${filter.color}` }}
              label={filters.getFilterLabel(filter)}
              onClick={() => filters.openFilter(filter.id)}
              onDelete={() => filters.removeAppliedFilter(filter.id)}
            />
          ))}
          {loading && <CircularProgress size={16} aria-label="Loading production data" />}
          {error && (
            <span role="alert" style={{ color: '#b42318', fontSize: 12 }}>
              {error}
            </span>
          )}
          {!loading && !error && filters.validFilters > 0 && !hasMatches && <span style={{ fontSize: 12 }}>No matching periods</span>}
        </Box>
        <Collapse in={filters.filtersOpen} unmountOnExit>
          <Box className="tableFilterPanel" sx={{ mt: 1 }}>
            {listFilters(filters.updateDraftFilters, filters.draftFilters, configuration, filters.highlightedFilterId, {
              disablePortal: false,
              sx: { zIndex: 'calc(var(--z-index-modal-content) + 1)' }
            })}
            {filters.filterError && (
              <div className="tableFilterError" role="alert">
                {filters.filterError}
              </div>
            )}
            <div className="tableFilterPanelFooter">
              <Button className="tableFilterClear" onClick={filters.clearFilters}>
                <FormattedMessage {...globalMessages.clear} />
              </Button>
              <Button className="tableFilterApply" variant="contained" onClick={filters.applyFilters}>
                <FormattedMessage {...modelFieldMessages.apply} />
              </Button>
            </div>
          </Box>
        </Collapse>
      </Box>
    </Portal>
  );
}
